/**
 * 地理分析：坐标归一、同地共现聚类、轨迹排序。
 * 不依赖任何在线底图，纯几何计算（可测试）。
 */
import type { Entity } from '../state/domain';

export type GeoPoint = {
  id: string;
  label: string;
  type: string;
  lat: number;
  lng: number;
  place: string;
  confidence: number;
  firstSeen: string | null;
  lastSeen: string | null;
};

export function geoPoints(entities: Entity[]): GeoPoint[] {
  const out: GeoPoint[] = [];
  for (const entity of entities) {
    const geo = entity.geo;
    if (!geo || typeof geo.lat !== 'number' || typeof geo.lng !== 'number') continue;
    if (Number.isNaN(geo.lat) || Number.isNaN(geo.lng)) continue;
    out.push({
      id: entity.id,
      label: entity.value ?? entity.label ?? entity.type,
      type: entity.type,
      lat: geo.lat,
      lng: geo.lng,
      place: geo.place ?? `${geo.lat.toFixed(3)}, ${geo.lng.toFixed(3)}`,
      confidence: entity.confidence,
      firstSeen: entity.firstSeen,
      lastSeen: entity.lastSeen,
    });
  }
  return out;
}

/** 球面距离（公里）。 */
export function haversineKm(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
): number {
  const R = 6371;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export type GeoCluster = {
  key: string;
  place: string;
  center: { lat: number; lng: number };
  points: GeoPoint[];
  /** 最大两点距离（公里） */
  radiusKm: number;
};

/** 按距离阈值做单链聚类（同地共现）。 */
export function clusterPoints(points: GeoPoint[], thresholdKm = 25): GeoCluster[] {
  const used = new Array<boolean>(points.length).fill(false);
  const clusters: GeoCluster[] = [];

  for (let i = 0; i < points.length; i++) {
    if (used[i]) continue;
    const bucket = [points[i]];
    used[i] = true;
    // 单链扩散
    let grew = true;
    while (grew) {
      grew = false;
      for (let j = 0; j < points.length; j++) {
        if (used[j]) continue;
        if (bucket.some(p => haversineKm(p, points[j]) <= thresholdKm)) {
          bucket.push(points[j]);
          used[j] = true;
          grew = true;
        }
      }
    }

    let maxDist = 0;
    for (let a = 0; a < bucket.length; a++) {
      for (let b = a + 1; b < bucket.length; b++) {
        maxDist = Math.max(maxDist, haversineKm(bucket[a], bucket[b]));
      }
    }
    const center = {
      lat: bucket.reduce((sum, p) => sum + p.lat, 0) / bucket.length,
      lng: bucket.reduce((sum, p) => sum + p.lng, 0) / bucket.length,
    };
    clusters.push({
      key: bucket.map(p => p.id).sort().join('|'),
      place: bucket[0].place,
      center,
      points: bucket,
      radiusKm: maxDist,
    });
  }

  return clusters.sort((a, b) => b.points.length - a.points.length);
}

/** 有首末时间的点按时间排序，得到可能的移动轨迹。 */
export function timelineTracks(points: GeoPoint[]): GeoPoint[] {
  return [...points]
    .filter(p => p.firstSeen || p.lastSeen)
    .sort((a, b) => {
      const ta = new Date(a.firstSeen ?? a.lastSeen ?? 0).getTime();
      const tb = new Date(b.firstSeen ?? b.lastSeen ?? 0).getTime();
      return ta - tb;
    });
}

export type GeoBounds = { minLat: number; maxLat: number; minLng: number; maxLng: number };

export function geoBounds(points: GeoPoint[], paddingRatio = 0.12): GeoBounds | null {
  if (points.length === 0) return null;
  const lats = points.map(p => p.lat);
  const lngs = points.map(p => p.lng);
  let minLat = Math.min(...lats);
  let maxLat = Math.max(...lats);
  let minLng = Math.min(...lngs);
  let maxLng = Math.max(...lngs);
  const padLat = Math.max(0.5, (maxLat - minLat) * paddingRatio);
  const padLng = Math.max(0.5, (maxLng - minLng) * paddingRatio);
  minLat -= padLat;
  maxLat += padLat;
  minLng -= padLng;
  maxLng += padLng;
  return { minLat, maxLat, minLng, maxLng };
}

/** 经纬度 → 归一化 [0,1] 平面坐标。 */
export function projectPoint(
  point: { lat: number; lng: number },
  bounds: GeoBounds
): { x: number; y: number } {
  const x = (point.lng - bounds.minLng) / Math.max(1e-9, bounds.maxLng - bounds.minLng);
  const y = 1 - (point.lat - bounds.minLat) / Math.max(1e-9, bounds.maxLat - bounds.minLat);
  return { x, y };
}

/** 生成经纬网刻度。 */
export function graticule(bounds: GeoBounds, steps = 5): { lat: number[]; lng: number[] } {
  const lat: number[] = [];
  const lng: number[] = [];
  for (let i = 0; i <= steps; i++) {
    lat.push(bounds.minLat + ((bounds.maxLat - bounds.minLat) * i) / steps);
    lng.push(bounds.minLng + ((bounds.maxLng - bounds.minLng) * i) / steps);
  }
  return { lat, lng };
}